// src/pages/AdminSearch.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import NumberPad from '../components/NumberPad';
import Popup     from '../components/Popup';
import { formatPhoneInput } from '../utils/phone';
import { parseCouponScan } from '../utils/couponScan';
import { resolveDisplayCode } from '../utils/redeemDisplayCode';
import CouponUsedNoticeModal from '../components/CouponUsedNoticeModal';
import MemberBarcodePanel from '../components/MemberBarcodePanel';

axios.defaults.withCredentials = true;

// 等級顯示：年會員且累計點數 >= 20 顯示「資深年會員」
// 注意：應使用 POINTS_EARNED（累計點數），而非 POINTS（目前可用點數）
const BASE_LEVELS = ['一般顧客', '年會員', '月會員', '員工'];
const getDisplayLevel = (level, pointsEarned) =>
  (level === '年會員' && (pointsEarned || 0) >= 20) ? '資深年會員' : (level || '一般顧客');

// 將數字包成 "數字"（避免 undefined/NaN）
const fmtQ = (n) => `"${Number.isFinite(+n) ? +n : 0}"`;

// 點數 / 日券 切換
function TargetSwitch({ value, onChange }) {
  const Item = ({ v, label }) => (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        border: '1px solid #ddd',
        borderRadius: 8,
        background: value === v ? '#eef6ff' : '#fff',
        cursor: 'pointer',
        fontSize: 20,
      }}
    >
      <input
        type="radio"
        name="target"
        value={v}
        checked={value === v}
        onChange={() => onChange(v)}
      />
      <span>{label}</span>
    </label>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <Item v="point" label="點數 Point" />
      <Item v="pass" label="日券 Day Pass" />
    </div>
  );
}

/** 內建數字鍵盤（搜尋用）—
 * 第一排：1 2 3 4 5 +
 * 第二排：6 7 8 9 0 刪除
 * ★ 做了防撐寬：minWidth:0 + minmax(0,1fr)
 */
function SearchKeypad({ onDigit, onDelete }) {
  const rowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 8,
  };

  return (
    <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
      <div style={rowStyle}>
        {['1','2','3','4','5','+'].map((k) => (
          <button key={k} type="button" className="btn btn-key" onClick={() => onDigit(k)}>{k}</button>
        ))}
      </div>
      <div style={rowStyle}>
        {['6','7','8','9','0'].map((k) => (
          <button key={k} type="button" className="btn btn-key" onClick={() => onDigit(k)}>{k}</button>
        ))}
        <button type="button" className="btn btn-key" onClick={onDelete}>刪除</button>
      </div>
    </div>
  );
}

// 簡單 Modal
function Modal({ open, onClose, children, width = 420 }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: width,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          padding: '16px 16px 12px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export default function AdminSearch() {
  const inputRef = useRef(null);
  const [scanMode, setScanMode] = useState(true);

  const [search, setSearch] = useState('');
  const [member, setMember] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [isEditing, setIsEditing] = useState(false);
  const [editedMember, setEditedMember] = useState(null); // { NAME, EMAIL, MEMBER_EXPIRY, BIRTHDAY }

  const [target, setTarget] = useState('point'); // 'point' | 'pass'
  const [amount, setAmount] = useState('1'); // 預設 1
  const [isDirty, setIsDirty] = useState(false);
  const [log, setLog] = useState(null);

  // 優惠券（核銷）
  const [memberCoupons, setMemberCoupons] = useState([]);
  const [couponMsg, setCouponMsg] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);

  // 等級歷史
  const [levelHistory, setLevelHistory] = useState([]);
  const [showLevelHistory, setShowLevelHistory] = useState(false);

  // —— 等級調整彈窗 —— //
  const [levelDlg, setLevelDlg] = useState({
    open: false,
    level: '年會員',
    start: '',
    expiry: '',
  });

  // 小工具：日期
  function todayYmd() {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }
  function addMonths(ymd, months) {
    const [y, m, d] = ymd.split('-').map((n) => +n);
    const dt = new Date(y, m - 1, d);
    dt.setMonth(dt.getMonth() + months);
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd2 = String(dt.getDate()).padStart(2, '0');
    return `${dt.getFullYear()}-${mm}-${dd2}`;
  }
  function addYears(ymd, years) {
    const [y, m, d] = ymd.split('-').map((n) => +n);
    const dt = new Date(y + years, m - 1, d);
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd2 = String(dt.getDate()).padStart(2, '0');
    return `${dt.getFullYear()}-${mm}-${dd2}`;
  }

  const openLevelDialog = () => {
    if (!member) return;
    const start = todayYmd();
    const lvl = member?.LEVEL || '一般顧客';
    let expiry = '';
    if (lvl === '年會員') expiry = addYears(start, 1);
    else if (lvl === '月會員') expiry = addMonths(start, 1);
    setLevelDlg({ open: true, level: lvl, start, expiry });
  };
  const handleLevelChange = (lvl) => {
    let expiry = '';
    if (lvl === '年會員') expiry = addYears(levelDlg.start || todayYmd(), 1);
    else if (lvl === '月會員') expiry = addMonths(levelDlg.start || todayYmd(), 1);
    setLevelDlg((p) => ({ ...p, level: lvl, expiry }));
  };
  const handleStartChange = (s) => {
    let expiry = levelDlg.expiry;
    if (levelDlg.level === '年會員') expiry = addYears(s, 1);
    else if (levelDlg.level === '月會員') expiry = addMonths(s, 1);
    else if (levelDlg.level === '一般顧客' || levelDlg.level === '員工') expiry = '';
    setLevelDlg((p) => ({ ...p, start: s, expiry }));
  };
  const submitLevelChange = async () => {
    try {
      const payload = {
        level: levelDlg.level,
        start_date: levelDlg.start || todayYmd(),
        expiry_date: levelDlg.expiry || null,
        note: '後台調整',
      };
      const { data } = await axios.post(
        `/api/admin/member/${member.ID}/level`,
        payload
      );
      if (data?.member) {
        setMember(data.member);
        await loadLevelHistory(data.member.ID);
      }
      setLevelDlg({ open: false, level: '年會員', start: '', expiry: '' });
      setMsg('等級已更新');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e?.response?.data?.error || '更新等級失敗');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  // 掃碼批次控制
  const searchRef = useRef('');
  useEffect(() => {
    searchRef.current = search;
  }, [search]);
  const scanBurstRef = useRef(false);

  // 初始 focus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  
  // 讀取網址列的 ?phone= 或 ?q= 自動查詢
  useEffect(() => {
    const usp = new URLSearchParams(window.location.search);
    const phone = usp.get('phone');
    const q = usp.get('q');
    const seedRaw = (phone || q || '').trim();
    if (seedRaw) {
      const seed = normalizePhoneInput(seedRaw);
      setSearch(seed);
      doSearch(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 掃碼體驗
  const handleSearchFocus = () => { try { inputRef.current?.select?.(); } catch {} };
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') { setSearch(''); scanBurstRef.current = false; return; }
    if (e.key === 'Enter')  { scanBurstRef.current = false; return; }
    if (scanMode) {
      const isChar = e.key?.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (isChar) {
        if (!scanBurstRef.current && searchRef.current) setSearch('');
        scanBurstRef.current = true;
      }
    }
  };

  const loadMemberCoupons = async (memberId) => {
    if (!memberId) return;
    try {
      const { data } = await axios.get(`/api/admin/member/${memberId}/coupons`);
      setMemberCoupons(Array.isArray(data?.rows) ? data.rows : []);
    } catch {
      setMemberCoupons([]);
      setCouponMsg('無法載入會員優惠券（請確認 /api/admin/member/:id/coupons）');
      setTimeout(() => setCouponMsg(''), 2500);
    }
  };

  const loadLevelHistory = async (memberId) => {
    if (!memberId) return;
    try {
      const { data } = await axios.get(`/api/admin/member/${memberId}/levels`);
      setLevelHistory(Array.isArray(data?.rows) ? data.rows : []);
    } catch {
      setLevelHistory([]);
    }
  };

  // 搜尋正規化：移除格式限制，允許任何輸入
  const normalizePhoneInput = (text) => formatPhoneInput(text);

  // 查詢
  const doSearch = async (qOverride) => {
    try {
      setErr(''); setLog(null); setMsg(''); setCouponMsg('');
      const raw = qOverride != null ? String(qOverride) : String(search || '');
      const q = raw.trim();
      if (!q) { setErr('請輸入搜尋內容'); return; }

      // 移除格式限制：嘗試先以電話搜尋，失敗則以 ID 搜尋
      const encoded = encodeURIComponent(q);
      let url = `/api/admin/member/phone/${encoded}`;
      let data;
      try {
        const res = await axios.get(url);
        data = res.data;
      } catch (phoneError) {
        // 如果電話搜尋失敗，嘗試 ID 搜尋
        url = `/api/admin/member/${encoded}`;
        const res = await axios.get(url);
        data = res.data;
      }
      setMember(data);
      setIsEditing(false);
      await loadMemberCoupons(data.ID);
      await loadLevelHistory(data.ID);

      if (scanMode) { setSearch(''); scanBurstRef.current = false; setTimeout(() => inputRef.current?.focus?.(), 0); }
      setAmount('1');
      setIsDirty(false);
    } catch {
      setMember(null);
      setMemberCoupons([]);
      setErr('查無此會員');
      if (scanMode) { scanBurstRef.current = false; setTimeout(() => inputRef.current?.focus?.(), 0); }
    }
  };

  // 一般資料編輯
  const handleEdit = () => {
    setEditedMember({
      NAME: member.NAME || '',
      EMAIL: member.EMAIL || '',
      MEMBER_EXPIRY: member.MEMBER_EXPIRY?.split?.('T')?.[0] || '',
      BIRTHDAY: member.BIRTHDAY?.split?.('T')?.[0] || member.BIRTHDAY || '',
    });
    setIsEditing(true);
    setMsg('');
  };
  const handleCancelEdit = () => { setIsEditing(false); setEditedMember(null); setMsg(''); };

  const handleSaveMember = async () => {
    const { NAME, EMAIL, MEMBER_EXPIRY, BIRTHDAY } = editedMember || {};
    const out = { NAME, EMAIL, MEMBER_EXPIRY, BIRTHDAY: BIRTHDAY || '' };
    try {
      await axios.put(`/api/admin/member/${member.ID}`, out);
      const { data } = await axios.get(`/api/admin/member/id/${member.ID}`);
      setMember(data);
      setIsEditing(false);
      setMsg('儲存成功');
    } catch { setMsg('儲存失敗'); }
    setTimeout(() => setMsg(''), 2000);
  };

  // ---------- 數字輸入 ----------
  const handleAmountInputChange = (val) => {
    const cleaned = String(val ?? '').replace(/[^\d]/g, '');
    setAmount(cleaned);
    if (!isDirty) setIsDirty(true);
  };
  const handlePadPress = (key) => {
    if (key === 'del') { setIsDirty(true); setAmount((prev) => String(prev || '').slice(0, -1)); return; }
    const digits = String(key).replace(/[^\d]/g, '');
    if (!digits) return;
    if (!isDirty) { setAmount(digits); setIsDirty(true); }
    else { setAmount((prev) => String(prev || '') + digits); }
  };

  // 通用操作（點數/日券）
  const postOperate = async (val, opType) => {
    const { data } = await axios.post(`/api/admin/member/${member.ID}/operate`, { type: opType || target, value: val });
      const { data: updated } = await axios.get(`/api/admin/member/id/${member.ID}`);
      setMember(updated);
      await loadLevelHistory(member.ID);
      setAmount('1');
      setIsDirty(false);
      return data?.msg || '操作完成';
  };

  // ====== 通用彈窗：點數/日券（發放/扣除） ======
  const [opConfirm, setOpConfirm] = useState({
    open: false, type: 'pass', action: 'use', amount: 0, busy: false, doneMsg: '',
  });

  const openOperationConfirm = (isAdd) => {
    if (!member) return;
    const amt = Math.abs(Number(amount) || 0);
    if (!amt) { setMsg('請輸入數字'); setTimeout(() => setMsg(''), 2000); return; }
    setOpConfirm({ open: true, type: target, action: isAdd ? 'add' : 'use', amount: amt, busy: false, doneMsg: '' });
  };

  const confirmOperation = async () => {
    const { type, action, amount: amt } = opConfirm;
    try {
      setOpConfirm((p) => ({ ...p, busy: true }));
      const val = action === 'add' ? +amt : -Math.abs(+amt);
      const text = await postOperate(val, type);
      const done =
        type === 'pass'
          ? (action === 'add' ? `已發放 ${amt} 張日券` : `已扣除 ${amt} 張日券`)
          : (action === 'add' ? `已增加 ${amt} 點`    : `已扣除 ${amt} 點`);
      setOpConfirm((p) => ({ ...p, busy: false, doneMsg: done }));
      setMsg(text || done);
    } catch (e) {
      setOpConfirm((p) => ({ ...p, busy: false }));
      setMsg(e?.response?.data?.error || '操作失敗');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  // ====== 優惠券核銷：改用彈窗 ======
  const [couponConfirm, setCouponConfirm] = useState({
    open: false, coupon: null, busy: false, doneMsg: '',
  });
  const [couponUsedNotice, setCouponUsedNotice] = useState({ open:false, couponName:'', claimId:'', displayCode:'', usedAt:'' });

  const redeemClaimCore = async (claimId) => {
    setCouponBusy(true);
    try {
      await axios.post(`/api/admin/coupon/claims/${Number(claimId)}/use`);
      if (member?.ID) await loadMemberCoupons(member.ID);
    } finally { setCouponBusy(false); }
  };

  const openCouponConfirmFromPreview = (claim) => {
    if (!claim) return;
    setCouponConfirm({
      open: true,
      coupon: {
        CLAIM_ID: claim.claimId,
        DISPLAY_CODE: claim.displayCode,
        NAME: claim.couponName,
        DESCRIPTION: claim.description,
        END_AT: claim.endAt,
        phoneTail: claim.phoneTail,
        emailMask: claim.emailMask,
        memberName: claim.memberName,
      },
      busy: false,
      doneMsg: '',
    });
  };

  const openCouponUsedNotice = (claim) => {
    if (!claim) return;
    setCouponUsedNotice({
      open: true,
      couponName: claim.couponName || claim.NAME || '',
      claimId: claim.claimId || claim.CLAIM_ID || '',
      displayCode: resolveDisplayCode(claim.claimId || claim.CLAIM_ID, claim.displayCode || claim.DISPLAY_CODE),
      usedAt: claim.usedAt ? String(claim.usedAt).replace('T', ' ').slice(0, 19) : '',
    });
  };

  const handleCouponScanInput = async (qRaw) => {
    const parsed = parseCouponScan(qRaw);
    if (!parsed) return false;
    setErr('');
    try {
      let claim;
      if (parsed.type === 'token') {
        const { data } = await axios.get(`/api/admin/coupon/claims/by-token/${encodeURIComponent(parsed.value)}`);
        claim = data?.claim;
      } else {
        const { data } = await axios.get(`/api/admin/coupon/claims/${parsed.value}/preview`);
        claim = data?.claim;
      }
      if (!claim) {
        setErr('找不到此優惠券');
        return true;
      }
      if (claim.isUsed) {
        openCouponUsedNotice(claim);
        return true;
      }
      if (claim.memberId) {
        try {
          const res = await axios.get(`/api/admin/member/${claim.memberId}`);
          setMember(res.data);
          await loadMemberCoupons(claim.memberId);
        } catch { /* 散客券無會員也可核銷 */ }
      } else {
        setMember(null);
        setMemberCoupons([]);
      }
      openCouponConfirmFromPreview(claim);
      return true;
    } catch (e) {
      setErr(e?.response?.data?.error === 'NOT_FOUND' ? '找不到此優惠券' : '優惠券查詢失敗');
      return true;
    }
  };

  const searchOrScan = async (qOverride) => {
    const qRaw = String(qOverride ?? search ?? '').trim();
    if (!qRaw) { setErr('請輸入搜尋內容'); return; }
    const couponHandled = await handleCouponScanInput(qRaw);
    if (couponHandled) {
      if (scanMode) {
        setSearch('');
        scanBurstRef.current = false;
        setTimeout(() => inputRef.current?.focus?.(), 0);
      }
      return;
    }
    await doSearch(qRaw);
  };

  const openCouponConfirm = (couponObj) => {
    if (!couponObj) return;
    setCouponConfirm({ open: true, coupon: couponObj, busy: false, doneMsg: '' });
  };

  const confirmRedeemCoupon = async () => {
    const c = couponConfirm.coupon; if (!c) return;
    try {
      setCouponConfirm((p) => ({ ...p, busy: true }));
      await redeemClaimCore(c.CLAIM_ID);
      setCouponConfirm((p) => ({ ...p, busy: false, doneMsg: `已核銷：${c.NAME}` }));
      setCouponMsg('核銷完成'); setTimeout(() => setCouponMsg(''), 2200);
    } catch (e) {
      setCouponConfirm((p) => ({ ...p, busy: false }));
      const status = e?.response?.status;
      const detail = e?.response?.data?.error || e?.message || '';
      if (status === 409 || detail === 'already_used' || detail === 'ALREADY_USED') {
        setCouponConfirm({ open: false, coupon: null, busy: false, doneMsg: '' });
        openCouponUsedNotice({
          couponName: c.NAME,
          claimId: c.CLAIM_ID,
          displayCode: resolveDisplayCode(c.CLAIM_ID, c.DISPLAY_CODE),
        });
        return;
      }
      setCouponMsg(`核銷失敗（${status || 'ERR'}：${detail || '請確認 /api/admin/coupon/claims/:id/use'}）`);
      setTimeout(() => setCouponMsg(''), 2200);
    }
  };

  const redeemClaim = async (claimId) => {
    const c = memberCoupons.find((x) => String(x.CLAIM_ID) === String(claimId));
    if (c) { openCouponConfirm(c); return; }
    openCouponConfirm({ CLAIM_ID: claimId, NAME: `#${claimId}` });
  };

  const openLog = async (tp) => {
    try {
      const { data } = await axios.get(`/api/admin/member/${member.ID}/history`);
      setLog({ type: tp, rows: tp === 'pass' ? data.pass : data.point });
    } catch { setMsg('無法載入明細'); setTimeout(() => setMsg(''), 2000); }
  };

  const onKeypadDigit  = (d) => { const next = normalizePhoneInput(search + d); setSearch(next); inputRef.current?.focus?.(); };
  const onKeypadDelete = () => {
    setSearch((prev) => { if (!prev) return ''; const cut = prev.slice(0, -1); return cut === '+' ? '' : cut; });
    inputRef.current?.focus?.();
  };

  // ======= 版面樣式 =======
  const wrapStyle = {
    display: 'grid',
    gridTemplateColumns: '1.15fr 0.85fr',
    gap: 16,
    padding: 12,
    fontSize: 16,
    lineHeight: 1.35,
  };
  const card = {
    padding: 12,
    border: '1px solid #e7e7e7',
    borderRadius: 10,
    background: '#fff',
  };
  const label = { color: '#555', fontSize: 23, whiteSpace: 'nowrap' };
  const valueBox = { fontWeight: 700, fontSize: 23 };

  // ======= UI =======
  return (
    <div className="admin-search-scroll">
      {/* 本頁滾動＋RWD 限制 */}
      <style>{`
        .admin-search-scroll{
          height:100%;
          min-height:0;                 /* 讓 1fr 容器可壓縮 */
          overflow-y:auto;              /* ★ 本頁滾動 */
          overflow-x:auto;              /* 寬內容可左右滑 */
          -webkit-overflow-scrolling:touch;
          overscroll-behavior:contain;
          scrollbar-gutter:stable both-edges;
          box-sizing:border-box;
        }
        @media (max-width:1100px){
          .admin-search-scroll .wrap{
            grid-template-columns:1fr !important; /* 兩欄→單欄 */
          }
        }
        .admin-search-scroll table{ width:100%; max-width:100%; table-layout:fixed; }
        .admin-search-scroll th,.admin-search-scroll td{
          word-break:break-word; overflow-wrap:anywhere; white-space:normal;
        }
        .admin-search-scroll img,.admin-search-scroll video,.admin-search-scroll pre,.admin-search-scroll code{
          max-width:100%; height:auto; white-space:pre-wrap; word-break:break-word; overflow-wrap:anywhere;
        }
        .admin-search-scroll button{
          font-family:inherit;
          font-weight:700;
          cursor:pointer;
          border-radius:8px;
        }
        .admin-search-scroll .btn{
          padding:10px 14px;
          font-size:20px;
        }
        .admin-search-scroll .btn-sm{
          padding:6px 10px;
          font-size:16px;
        }
        .admin-search-scroll .btn-key{
          height:34px;
          min-width:0;
          padding:0 4px;
          font-size:16px;
          background:#fff !important;
          color:#1f2937 !important;
          border:1px solid #9ca3af !important;
        }
        .admin-search-scroll .btn-primary{
          background:#7b4f2f !important;
          color:#fff !important;
          border:1px solid #5e3a1a !important;
        }
        .admin-search-scroll .btn-secondary{
          background:#f3f4f6 !important;
          color:#1f2937 !important;
          border:1px solid #9ca3af !important;
        }
        .admin-search-scroll .btn-success{
          background:#15803d !important;
          color:#fff !important;
          border:1px solid #166534 !important;
        }
        .admin-search-scroll .btn-danger{
          background:#b91c1c !important;
          color:#fff !important;
          border:1px solid #991b1b !important;
        }
        .admin-search-scroll .btn-info{
          background:#2563eb !important;
          color:#fff !important;
          border:1px solid #1d4ed8 !important;
        }
        .admin-search-scroll .btn-dark{
          background:#111827 !important;
          color:#fff !important;
          border:1px solid #111827 !important;
        }
        .admin-search-scroll button:disabled{
          opacity:.55;
          cursor:not-allowed;
        }
      `}</style>

      <div className="wrap" style={wrapStyle}>
        {/* 左：搜尋 + 會員資料 */}
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ ...card }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                ref={inputRef}
                type="tel"
                inputMode="numeric"
                style={{
                  flex: 1,
                  minWidth: 260,
                  padding: '10px 12px',
                  fontSize: 23,
                  border: '1px solid #ccc',
                  borderRadius: 8,
                }}
                placeholder="掃條碼／優惠券 QR 或輸入電話"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { handleSearchKeyDown(e); if (e.key === 'Enter') searchOrScan(search); }}
                onFocus={handleSearchFocus}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setSearch((prev) => {
                  const raw = String(prev || '').trim();
                  if (raw.startsWith('+886')) return raw;
                  const digits = raw.replace(/[^\d]/g, '').replace(/^886/, '').replace(/^0+/, '');
                  return `+886${digits}`;
                })}
              >
                +886
              </button>
              <button type="button" onClick={() => searchOrScan(search)} className="btn btn-primary">
                搜尋
              </button>
              <button
                type="button"
                onClick={() => { setSearch(''); scanBurstRef.current = false; inputRef.current?.focus?.(); }}
                className="btn btn-secondary"
              >
                清空
              </button>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 20, userSelect: 'none', marginLeft: 'auto' }}
              >
                <input
                  type="checkbox"
                  checked={scanMode}
                  onChange={(e) => { setScanMode(e.target.checked); scanBurstRef.current = false; }}
                />
                掃碼模式
              </label>
            </div>

            <SearchKeypad onDigit={onKeypadDigit} onDelete={onKeypadDelete} />

            {err && (<div style={{ color: '#b00020', marginTop: 8, fontWeight: 700 }}>{err}</div>)}
          </div>

          {member && (
            <div style={{ ...card }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {/* 左欄 */}
                <div style={{ display: 'grid', gap: 10 }}>
                  <div>
                    <div style={label}>姓名</div>
                    <div style={valueBox}>
                      {isEditing ? (
                        <input
                          style={{ width: '100%', padding: '8px', fontSize: 23, border: '1px solid #ccc', borderRadius: 8 }}
                          value={editedMember?.NAME || ''}
                          onChange={(e) => setEditedMember((m) => ({ ...(m || {}), NAME: e.target.value }))}
                        />
                      ) : (member.NAME || '—')}
                    </div>
                  </div>

                  <div>
                    <div style={label}>等級</div>
                    <div style={{ ...valueBox, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span>{member.DISPLAY_LEVEL || getDisplayLevel(member.LEVEL, member.POINTS_EARNED || 0)}</span>
                      <button type="button" onClick={openLevelDialog} className="btn btn-sm btn-primary">
                        調整會員身分
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowLevelHistory(true)}
                        className="btn btn-sm btn-info"
                      >
                        等級歷史
                      </button>
                    </div>
                  </div>

                  <div>
                    <div style={label}>Email</div>
                    <div style={valueBox}>
                      {isEditing ? (
                        <input
                          style={{ width: '100%', padding: '8px', fontSize: 23, border: '1px solid #ccc', borderRadius: 8 }}
                          value={editedMember?.EMAIL || ''}
                          onChange={(e) => setEditedMember((m) => ({ ...(m || {}), EMAIL: e.target.value }))}
                        />
                      ) : (member.EMAIL || '—')}
                    </div>
                  </div>
                </div>

                {/* 右欄 */}
                <div style={{ display: 'grid', gap: 10 }}>
                  <div>
                    <div style={label}>電話</div>
                    <div style={{ ...valueBox, fontFeatureSettings: '"tnum"' }}>{member.PHONE || '—'}</div>
                  </div>

                  <div>
                    <div style={label}>生日</div>
                    <div style={valueBox}>
                      {isEditing ? (
                        <input
                          type="date"
                          style={{ width: '100%', padding: '8px', fontSize: 23, border: '1px solid #ccc', borderRadius: 8 }}
                          value={editedMember?.BIRTHDAY?.split?.('T')?.[0] || editedMember?.BIRTHDAY || ''}
                          onChange={(e) => setEditedMember((m) => ({ ...(m || {}), BIRTHDAY: e.target.value }))}
                        />
                      ) : (member.BIRTHDAY?.split?.('T')?.[0] || member.BIRTHDAY || '—')}
                    </div>
                  </div>

                  <div>
                    <div style={label}>會員到期</div>
                    <div style={valueBox}>
                      {isEditing ? (
                        <input
                          type="date"
                          style={{ width: '100%', padding: '8px', fontSize: 25, border: '1px solid #ccc', borderRadius: 8 }}
                          value={editedMember?.MEMBER_EXPIRY?.split?.('T')?.[0] || ''}
                          onChange={(e) => setEditedMember((m) => ({ ...(m || {}), MEMBER_EXPIRY: e.target.value }))}
                        />
                      ) : (member.MEMBER_EXPIRY?.split?.('T')?.[0] || '—')}
                    </div>
                  </div>
                </div>
              </div>

              {/* 點數 / 日券 快捷 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 10,
                  marginTop: 12,
                  background: '#fafafa',
                  border: '1px solid #eee',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div>
                  <div style={label}>點數</div>
                  <div style={{ fontSize: 30, fontWeight: 800, fontFeatureSettings: '"tnum"' }}>
                    {member.POINTS}（累計{member.POINTS_EARNED}）
                    <button type="button" onClick={() => openLog('point')} className="btn btn-sm btn-secondary" style={{ marginLeft: 6 }}>明細</button>
                  </div>
                </div>
                <div>
                  <div style={label}>日券</div>
                  <div style={{ fontSize: 30, fontWeight: 800 }}>
                    {member.DAY_PASS} 張 <button type="button" onClick={() => openLog('pass')} className="btn btn-sm btn-secondary" style={{ marginLeft: 6 }}>明細</button>
                  </div>
                </div>
              </div>

              <MemberBarcodePanel member={member} />

              {/* 編輯控制 */}
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                {!isEditing ? (
                  <button type="button" onClick={handleEdit} className="btn btn-primary">
                    編輯會員資料
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={handleSaveMember} className="btn btn-success">
                      儲存
                    </button>
                    <button type="button" onClick={handleCancelEdit} className="btn btn-secondary">
                      取消
                    </button>
                  </>
                )}
                {msg && (
                  <div style={{ color: '#0a7', fontWeight: 700, marginLeft: 'auto', alignSelf: 'center' }}>
                    {msg}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 右：數字鍵盤操作 + 優惠券核銷 */}
        {member && (
          <div style={{ display: 'grid', gap: 12 }}>
            {/* 點數 / 日券操作 */}
            <div style={{ ...card, background: '#fafafa' }}>
              <input
                value={amount}
                placeholder="輸入數字"
                onChange={(e) => handleAmountInputChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: 10,
                  fontSize: 18,
                  textAlign: 'right',
                  border: '1px solid #ccc',
                  borderRadius: 8,
                  marginBottom: 10,
                }}
              />
              <div style={{ display: 'flex', gap: 12 }}>
                <NumberPad value={amount} onChange={handlePadPress} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                  <TargetSwitch value={target} onChange={setTarget} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* 一律走彈窗 */}
                    <button
                      type="button"
                      onClick={() => openOperationConfirm(true)}
                      className="btn btn-success"
                      style={{ flex: 1 }}
                    >
                      加值／發放
                    </button>
                    <button
                      type="button"
                      onClick={() => openOperationConfirm(false)}
                      className="btn btn-danger"
                      style={{ flex: 1 }}
                    >
                      使用扣除
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 優惠券核銷 */}
            <div style={{ ...card, background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontSize: 18 }}>優惠券</h4>
                {couponMsg && <div style={{ color: '#0a7', fontWeight: 700 }}>{couponMsg}</div>}
              </div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
                上方搜尋框可掃描核銷 QR 或輸入兌換編號
              </div>

              <div style={{ maxHeight: 300, overflow: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%', border: '1px solid #eee', borderRadius: 8 }}>
                <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                    <tr>
                      <th align="left" style={{ padding: '8px 10px' }}>兌換編號</th>
                      <th align="left" style={{ padding: '8px 10px' }}>名稱</th>
                      <th align="left" style={{ padding: '8px 10px' }}>有效期</th>
                      <th align="left" style={{ padding: '8px 10px' }}>領取</th>
                      <th align="left" style={{ padding: '8px 10px' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberCoupons.filter((c) => !c.USED_AT).map((c) => (
                      <tr key={String(c.CLAIM_ID)}>
                        <td style={{ padding: '8px 10px', fontFeatureSettings: '"tnum"' }}>
                          {resolveDisplayCode(c.CLAIM_ID, c.DISPLAY_CODE)}
                        </td>
                        <td style={{ padding: '8px 10px' }}>{c.NAME}</td>
                        <td style={{ padding: '8px 10px' }}>{(c.START_AT || '') + ' ~ ' + (c.END_AT || '')}</td>
                        <td style={{ padding: '8px 10px' }}>{(c.CLAIMED_AT || '').replace('T', ' ').slice(0, 19)}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <button type="button" disabled={couponBusy} onClick={() => redeemClaim(c.CLAIM_ID)} className="btn btn-sm btn-primary">
                            {couponBusy ? '處理中…' : '核銷'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {memberCoupons.filter((c) => !c.USED_AT).length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: '10px', opacity: 0.6 }}>沒有未使用的優惠券</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 明細彈窗 */}
        {log && (
          <Popup onClose={() => setLog(null)}>
            <h3 style={{ marginTop: 0 }}>{log.type === 'pass' ? '日券明細' : '點數明細'}</h3>
            <div style={{ maxHeight: '60vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%', border: '1px solid #eee', borderRadius: 8 }}>
              <table style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr>
                    <th>時間</th>
                    <th>{log.type === 'point' ? '事件' : '類型'}</th>
                    <th>{log.type === 'point' ? '點數' : '數量'}</th>
                  </tr>
                </thead>
                <tbody>
                  {log.rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ padding: '6px 8px' }}>{(r.T || r.CREATED_TIME).replace('T', ' ').slice(0, 19)}</td>
                      <td style={{ padding: '6px 8px' }}>{r.TYPE || r.EVENT}</td>
                      <td style={{ padding: '6px 8px' }}>{r.AMOUNT ?? r.POINTS}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Popup>
        )}

        {/* 通用操作確認視窗：點數/日券（發放/扣除） → 放大版 */}
        <Modal
          open={opConfirm.open}
          onClose={() => setOpConfirm({ open: false, type: 'pass', action: 'use', amount: 0, busy: false, doneMsg: '' })}
          width={900}
        >
          {!opConfirm.doneMsg ? (
            <>
              <h3 style={{ margin: '8px 0 14px', fontSize: 32, fontWeight: 900 }}>
                確認
                {opConfirm.action === 'add'
                  ? (opConfirm.type === 'pass' ? '發放日券' : '增加點數')
                  : (opConfirm.type === 'pass' ? '扣除日券' : '扣除點數')}
              </h3>
              <div style={{ marginBottom: 18, fontSize: 22 }}>
                確定要
                <b style={{ fontSize: 28, margin: '0 6px' }}>
                  {opConfirm.action === 'add'
                    ? (opConfirm.type === 'pass' ? '發放' : '增加')
                    : '扣除'}
                  {opConfirm.type === 'pass' ? ` ${opConfirm.amount} 張日券` : ` ${opConfirm.amount} 點`}
                </b>
                嗎？
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setOpConfirm({ open: false, type: 'pass', action: 'use', amount: 0, busy: false, doneMsg: '' })}
                  disabled={opConfirm.busy}
                  className="btn btn-secondary"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={confirmOperation}
                  disabled={opConfirm.busy}
                  className="btn btn-dark"
                >
                  {opConfirm.busy ? '處理中…' : '確認'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ margin: '8px 0 14px', fontSize: 28, fontWeight: 900 }}>已完成</h3>
              <div style={{ color: '#0a7', fontWeight: 800, marginBottom: 18, fontSize: 22 }}>
                {opConfirm.doneMsg}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setOpConfirm({ open: false, type: 'pass', action: 'use', amount: 0, busy: false, doneMsg: '' })}
                  className="btn btn-primary"
                >
                  關閉
                </button>
              </div>
            </>
          )}
        </Modal>

        {/* 優惠券核銷確認視窗（保持原尺寸） */}
        <Modal
          open={couponConfirm.open}
          onClose={() => setCouponConfirm({ open: false, coupon: null, busy: false, doneMsg: '' })}
        >
          {!couponConfirm.doneMsg ? (
            <>
              <h3 style={{ margin: '6px 0 10px', fontWeight: 900 }}>確認兌換優惠券</h3>
              {(member?.NAME || couponConfirm.coupon?.memberName) && (
                <div style={{ marginBottom: 8, fontSize: 16 }}>
                  會員：<b>{member?.NAME || couponConfirm.coupon?.memberName}</b>
                </div>
              )}
              <div style={{ marginBottom: 8, fontSize: 16 }}>
                優惠券：<b>{couponConfirm.coupon?.NAME || `#${couponConfirm.coupon?.CLAIM_ID}`}</b>
              </div>
              <div style={{ marginBottom: 8, fontSize: 22, fontWeight: 900 }}>
                兌換編號 {resolveDisplayCode(couponConfirm.coupon?.CLAIM_ID, couponConfirm.coupon?.DISPLAY_CODE)}
              </div>
              {couponConfirm.coupon?.END_AT && (
                <div style={{ marginBottom: 8, fontSize: 14, color: '#555' }}>到期 {couponConfirm.coupon.END_AT}</div>
              )}
              {(couponConfirm.coupon?.phoneTail) && (
                <div style={{ marginBottom: 8, fontSize: 14, color: '#555' }}>
                  手機末四碼 {couponConfirm.coupon.phoneTail}
                </div>
              )}
              <div style={{ margin: '6px 0 14px', fontSize: 14, color: '#555' }}>已提醒工作人員優惠內容</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" onClick={() => setCouponConfirm({ open: false, coupon: null, busy: false, doneMsg: '' })} disabled={couponConfirm.busy} className="btn btn-secondary">
                  取消
                </button>
                <button type="button" onClick={confirmRedeemCoupon} disabled={couponConfirm.busy} className="btn btn-primary">
                  {couponConfirm.busy ? '處理中…' : '確認兌換'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ margin: '6px 0 10px' }}>已完成</h3>
              <div style={{ color: '#0a7', fontWeight: 800, marginBottom: 6, fontSize: 16 }}>{couponConfirm.doneMsg}</div>
              <div style={{ marginBottom: 14, fontSize: 14, color: '#555' }}>已提醒工作人員優惠內容</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setCouponConfirm({ open: false, coupon: null, busy: false, doneMsg: '' })} className="btn btn-primary">
                  關閉
                </button>
              </div>
            </>
          )}
        </Modal>

        <CouponUsedNoticeModal
          open={couponUsedNotice.open}
          onClose={() => setCouponUsedNotice({ open:false, couponName:'', claimId:'', displayCode:'', usedAt:'' })}
          couponName={couponUsedNotice.couponName}
          displayCode={couponUsedNotice.displayCode}
          usedAt={couponUsedNotice.usedAt}
        />

        {/* 等級歷史彈窗 */}
        <Modal
          open={showLevelHistory}
          onClose={() => setShowLevelHistory(false)}
          width={800}
        >
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>會員等級歷史</h3>
          <div style={{ maxHeight: '60vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%', border: '1px solid #eee', borderRadius: 8 }}>
            {levelHistory.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>沒有等級歷史紀錄</div>
            ) : (
              <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 14 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '10px', borderBottom: '2px solid #ddd', fontWeight: 700 }}>等級</th>
                    <th style={{ textAlign: 'left', padding: '10px', borderBottom: '2px solid #ddd', fontWeight: 700 }}>起始日</th>
                    <th style={{ textAlign: 'left', padding: '10px', borderBottom: '2px solid #ddd', fontWeight: 700 }}>到期日</th>
                    <th style={{ textAlign: 'left', padding: '10px', borderBottom: '2px solid #ddd', fontWeight: 700 }}>備註</th>
                    <th style={{ textAlign: 'left', padding: '10px', borderBottom: '2px solid #ddd', fontWeight: 700 }}>建立時間</th>
                  </tr>
                </thead>
                <tbody>
                  {levelHistory.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px' }}>{r.LEVEL || '—'}</td>
                      <td style={{ padding: '10px' }}>{r.START_DATE ? String(r.START_DATE).slice(0, 10) : '—'}</td>
                      <td style={{ padding: '10px' }}>{r.EXPIRY_DATE ? String(r.EXPIRY_DATE).slice(0, 10) : '—'}</td>
                      <td style={{ padding: '10px' }}>{r.NOTE || '—'}</td>
                      <td style={{ padding: '10px' }}>{r.CREATED_AT ? String(r.CREATED_AT).replace('T', ' ').slice(0, 19) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              onClick={() => setShowLevelHistory(false)}
              className="btn btn-secondary"
            >
              關閉
            </button>
          </div>
        </Modal>

        {/* 調整會員身分彈窗 */}
        <Modal
          open={levelDlg.open}
          onClose={() => setLevelDlg({ open: false, level: '年會員', start: '', expiry: '' })}
          width={460}
        >
          <h3 style={{ margin: '6px 0 12px' }}>調整會員身分</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <div style={{ fontSize: 14, color: '#555', marginBottom: 4 }}>會員</div>
              <div style={{ fontWeight: 700 }}>{member?.NAME || '—'}（{member?.PHONE || '—'}）</div>
            </div>

            <div>
              <div style={{ fontSize: 14, color: '#555', marginBottom: 4 }}>等級</div>
              <select
                value={levelDlg.level}
                onChange={(e) => handleLevelChange(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: 16, border: '1px solid #ccc', borderRadius: 8 }}
              >
                {BASE_LEVELS.map((l) => (<option key={l} value={l}>{l}</option>))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 14, color: '#555', marginBottom: 4 }}>開始日</div>
                <input
                  type="date"
                  value={levelDlg.start || todayYmd()}
                  onChange={(e) => handleStartChange(e.target.value)}
                  style={{ width: '100%', padding: '8px', fontSize: 16, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </div>
              <div>
                <div style={{ fontSize: 14, color: '#555', marginBottom: 4 }}>到期日</div>
                <input
                  type="date"
                  value={levelDlg.expiry || ''}
                  onChange={(e) => setLevelDlg((p) => ({ ...p, expiry: e.target.value }))}
                  placeholder="依等級自動帶出，可自行調整"
                  style={{ width: '100%', padding: '8px', fontSize: 16, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" onClick={() => setLevelDlg({ open: false, level: '年會員', start: '', expiry: '' })} className="btn btn-secondary">
              取消
            </button>
            <button type="button" onClick={submitLevelChange} className="btn btn-primary">
              確認
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
}
